import { FAMILIES, familyScale, type FamilyId, type FontFace, type FontStem } from "./fonts";

// Port of the Nohaya Career app's render_theme.dart.

/** An RGB colour with 0..1 channels, like the `pdf` package's PdfColor. */
export type Rgb = { r: number; g: number; b: number };

export function rgb(argb: number): Rgb {
  return { r: ((argb >> 16) & 0xff) / 255, g: ((argb >> 8) & 0xff) / 255, b: (argb & 0xff) / 255 };
}

export const WHITE: Rgb = { r: 1, g: 1, b: 1 };
export const BLACK: Rgb = { r: 0, g: 0, b: 0 };
/** PdfColors.grey500 */
export const GREY_500: Rgb = rgb(0xff9e9e9e);

export function hex(c: Rgb): string {
  const to = (v: number) => Math.round(Math.min(1, Math.max(0, v)) * 255).toString(16).padStart(2, "0");
  return `#${to(c.r)}${to(c.g)}${to(c.b)}`;
}

export const Pal = {
  mix(a: Rgb, b: Rgb, t: number): Rgb {
    return { r: a.r + (b.r - a.r) * t, g: a.g + (b.g - a.g) * t, b: a.b + (b.b - a.b) * t };
  },
  tint(c: Rgb, t: number): Rgb {
    return Pal.mix(c, WHITE, t);
  },
  shade(c: Rgb, t: number): Rgb {
    return Pal.mix(c, BLACK, t);
  },
  luminance(c: Rgb): number {
    return 0.2126 * c.r + 0.7152 * c.g + 0.0722 * c.b;
  },
  isDark(c: Rgb): boolean {
    return Pal.luminance(c) < 0.55;
  },
  /** Text colour that reads on [bg]. */
  on(bg: Rgb): Rgb {
    return Pal.isDark(bg) ? WHITE : rgb(0xff111827);
  },
  /** Deepens very light accents so white text on them keeps contrast. */
  deep(c: Rgb): Rgb {
    let out = c;
    for (let guard = 0; Pal.luminance(out) > 0.42 && guard < 8; guard++) out = Pal.shade(out, 0.18);
    return out;
  },
};

export type TextStyle = {
  font: FontStem;
  size: number;
  color: Rgb;
  letterSpacing: number;
  /** Extra space between wrapped lines (pt), as in the `pdf` package. */
  lineSpacing: number;
};

export type StyleOpts = { color?: Rgb; letterSpacing?: number; lineSpacing?: number };

/** Text colours must read on white: deepen light picks (the app's `_legible`). */
function legible(c: Rgb): Rgb {
  let out = c;
  for (let guard = 0; Pal.luminance(out) > 0.32 && guard < 10; guard++) out = Pal.shade(out, 0.18);
  return out;
}

/** Fonts, colours, sizes and rhythm for one render. */
export class RenderTheme {
  readonly body: FontFace;
  readonly heading: FontFace;
  readonly display: FontFace;
  readonly mono: FontFace;
  readonly accent: Rgb;
  /** Font-size multiplier. */
  readonly scale: number;
  /** Vertical-rhythm multiplier (compact < 1 < relaxed). */
  readonly density: number;
  /**
   * Optical correction of a separately chosen heading/display font relative
   * to the body font (1 when they're the template's own pairing).
   */
  readonly headingScale: number;

  /** Darkest text (names, titles), body text, secondary and faint text. */
  readonly ink: Rgb;
  readonly text: Rgb;
  readonly muted: Rgb;
  readonly faint: Rgb;
  readonly line = rgb(0xffe2e8f0);

  readonly accentText: Rgb;
  readonly accentTint: Rgb;
  readonly accentSoft: Rgb;

  private readonly headingStems: Set<FontStem>;

  constructor(opts: {
    body: FamilyId;
    heading: FamilyId;
    display: FamilyId;
    mono: FamilyId;
    accent: Rgb;
    fontScale?: number;
    density?: number;
    headingScale?: number;
    /** User text colour; null keeps the default slate ink. */
    textColor?: Rgb | null;
  }) {
    this.body = FAMILIES[opts.body];
    this.heading = FAMILIES[opts.heading];
    this.display = FAMILIES[opts.display];
    this.mono = FAMILIES[opts.mono];
    this.accent = opts.accent;
    this.scale = (opts.fontScale ?? 1) * familyScale(opts.body);
    this.density = opts.density ?? 1;
    this.headingScale = opts.headingScale ?? 1;
    const tc = opts.textColor ? legible(opts.textColor) : null;
    this.ink = tc ?? rgb(0xff111827);
    this.text = tc ? Pal.tint(tc, 0.14) : rgb(0xff334155);
    this.muted = tc ? Pal.tint(tc, 0.42) : rgb(0xff64748b);
    this.faint = tc ? Pal.tint(tc, 0.58) : rgb(0xff94a3b8);
    this.accentText = Pal.luminance(this.accent) > 0.6 ? Pal.shade(this.accent, 0.35) : this.accent;
    this.accentTint = Pal.tint(this.accent, 0.9);
    this.accentSoft = Pal.tint(this.accent, 0.72);
    const h = this.heading;
    const d = this.display;
    this.headingStems = new Set([h.regular, h.italic, h.semiBold, h.bold, h.extraBold, d.regular, d.semiBold, d.bold, d.extraBold]);
  }

  fs(size: number): number {
    return size * this.scale;
  }

  gap(value: number): number {
    return value * this.density;
  }

  /** Extra leading between wrapped lines, scaled with size and density. */
  leading(size: number): number {
    return this.fs(size) * (0.22 + 0.08 * this.density);
  }

  style(font: FontStem, size: number, o: StyleOpts = {}): TextStyle {
    const heading = this.headingScale !== 1 && this.headingStems.has(font);
    return {
      font,
      size: heading ? this.fs(size) * this.headingScale : this.fs(size),
      color: o.color ?? this.text,
      letterSpacing: o.letterSpacing ?? 0,
      lineSpacing: o.lineSpacing ?? this.leading(size),
    };
  }
}
